<template>
  <v-col cols="12" class="pa-0">
    <v-row no-gutters style="min-height: 28px">
      <v-col
        cols="auto"
        class="ml-n3"
        :class="{
          'geometry-input-value': true,
          'geometry-input-value-empty': !hasValue,
          'geometry-input-value-added': hasValue,
        }"
        @click="computedAction"
      >
        {{ addGeometryText }}
        <span style="color: red; padding-left: 2px">{{ hasValue ? '' : '*' }} </span>
      </v-col>

      <v-col cols="auto" v-if="hasValue && hasCustomButton">
        <v-tooltip bottom color="font">
          <template #activator="{ on }">
            <div v-on="on">
              <base-button-subheader
                :translation-path="customButtonAction.text"
                :loading="customButtonAction.loading"
                :disabled="customButtonAction.disabled"
                @click="customButtonAction.action"
              />
            </div>
          </template>
          {{ $i18n.t(customButtonAction.text) }}
        </v-tooltip>
      </v-col>

      <v-col class="geometry-input-value mx-2 py-2" cols="auto" v-if="hasAssignValue && isAssignGeometryEnabled">
        {{ $i18n.t('default.or').toLowerCase() }}
      </v-col>
      <v-col
        cols="auto"
        v-if="hasAssignValue && isAssignGeometryEnabled"
        :class="{
          'geometry-input-value': true,
          'geometry-input-value-empty': !hasValue,
          'geometry-input-value-added': hasValue,
        }"
        @click="assignGeometry"
      >
        {{ computedAssignGeometryText }}
        <span style="color: red; padding-left: 2px">{{ hasValue ? '' : '*' }} </span>
      </v-col>

      <v-spacer />
      <v-col cols="auto" v-if="computedValue">
        <dots-menu v-if="isEditingOn" :items="dotsMenuItems" @delete="deleteGeometry"></dots-menu>
      </v-col>
    </v-row>
    <v-row no-gutters class="geometry-input-hint">
      <v-col cols="auto">
        {{ hasValue ? (hintWithValue ? hintWithValue : '') : hint }}
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { call, get } from 'vuex-pathify';

import { never } from 'ol/events/condition';

import { prepareDrawnGeometry } from '@/assets/js/mapUtils';
import DotsMenu from '@/components/DotsMenu';

export default {
  name: 'SidebarGeometryInput',
  components: {
    DotsMenu,
  },
  props: {
    hint: {
      type: String,
      default: '',
    },
    hintWithValue: {
      type: String,
      default: '',
    },
    value: {
      required: true,
    },
    geometryType: {
      type: String,
      required: true,
    },
    assignGeometryType: {
      type: String,
      required: false,
    },
    name: {
      type: String,
      required: true,
    },
    isEditingOn: {
      type: Boolean,
      default: true,
    },
    hasAssignGeometry: {
      type: Boolean,
      default: false,
    },
    addedGeometryText: {
      type: String,
      default: 'dialog.addedGeometry',
    },
    drawGeometryText: {
      type: String,
      default: 'dialog.addGeometry',
    },
    assignGeometryText: {
      type: String,
      default: 'dialog.assignGeometry',
    },
    isValid: {
      type: Boolean,
      default: false,
    },
    customButtonAction: {
      type: Object,
    },
    hasPadding: {
      type: Boolean,
      default: true,
    },
    turnOffModuleIdentificationOnDraw: {
      type: Boolean,
      default: false,
    },
    hasMarkerSymbolization: {
      type: Boolean,
      default: false,
    },
    disableAddingNewVertices: {
      type: Boolean,
      default: false,
    },
    maxPoints: {
      type: Number,
    },
  },
  data: () => ({
    dotsMenuItems: [
      {
        name: 'delete',
      },
    ],
  }),
  computed: {
    isParcelsEnabled: get('admin/additionalModules@PARCELS.enabled'),
    isModuleEnabled: get('admin/settingsValues@parcels_module_enabled'),
    selectedDataSource: get('admin/modulesMapping@parcels.parcels_datasource'),
    settingsParcelsProvider: get('admin/settingsValues@parcels_provider'),
    isAssignGeometryEnabled() {
      if (!this.settingsParcelsProvider || !this.isModuleEnabled || !this.isParcelsEnabled) {
        return false;
      }
      if (this.settingsParcelsProvider === 'uldk') {
        return true;
      } else {
        for (const v of Object.values(this.selectedDataSource || {})) {
          if (!v) {
            return false;
          }
        }
        return true;
      }
    },
    computedAction() {
      return this.hasValue ? this.zoomToGeometry : this.addGeometry;
    },
    computedValue: {
      get() {
        return this.value;
      },
      set(nV) {
        this.$emit('input', nV);
      },
    },
    hasCustomButton() {
      return !!this.customButtonAction;
    },
    hasValue() {
      return !!this.computedValue;
    },
    hasAssignValue() {
      return this.hasAssignGeometry ? !this.hasValue : this.hasAssignGeometry;
    },
    addGeometryText() {
      return this.hasValue ? this.$i18n.t(this.addedGeometryText) : this.$i18n.t(this.drawGeometryText);
    },
    computedAssignGeometryText() {
      return this.$i18n.t(this.assignGeometryText);
    },
  },
  watch: {
    value(nV) {
      this.$emit('update:isValid', !!nV);
    },
    isEditingOn(nV) {
      if (nV) {
        this.$root.$emit('modifySidebarGeometry', {
          geometryValue: this.value,
          modifyendCallback: this.onModifyend,
          withEditing: true,
          isInitSnapping: false,
          insertVertexCondition: never,
        });
      } else {
        this.$root.$emit('deleteSidebarGeometry');
      }
    },
  },
  methods: {
    getFeature: call('layers/getDataSourceFeature'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    setSidebarGeometry(feature) {
      if (!feature) {
        this.computedValue = null;
      } else {
        this.computedValue = prepareDrawnGeometry(feature);
      }
    },
    onModifyend(e) {
      this.setSidebarGeometry(e.features.getArray()[0]);
    },
    onDrawend(e) {
      this.setSidebarGeometry(e.feature);
      this.$emit('drawEnd');
    },
    addGeometry() {
      if (this.hasValue) {
        return;
      }
      if (this.turnOffModuleIdentificationOnDraw) {
        this.$root.$emit('moduleIdentification-action', { isActive: false, moduleRoute: '' });
      }
      this.$root.$emit('drawSidebarGeometry', {
        geometryType: this.geometryType,
        drawendCallback: this.onDrawend,
        modifyendCallback: this.onModifyend,
        withEditing: true,
        marker: this.hasMarkerSymbolization,
        isInitSnapping: false,
        maxPoints: this.maxPoints,
        insertVertexCondition: this.disableAddingNewVertices ? never : undefined,
      });
    },
    assignGeometry() {
      if (this.hasValue) {
        return;
      }
      this.$root.$emit('identification-action', true, { specialType: 'assignPlot' });
    },
    deleteGeometry() {
      if (!this.computedValue) {
        return;
      }
      this.$root.$emit('deleteSidebarGeometry');
      this.computedValue = null;
    },
    zoomToGeometry() {
      if (!this.computedValue) {
        return;
      }
      this.$root.$emit('createGeomAndFitView', this.computedValue);
    },
    refreshObjectGeometry() {
      this.$root.$emit('deleteSidebarGeometry');
    },
    onBeforeDestroy() {
      this.$root.$emit('deleteSidebarGeometry');
      this.$root.$off('refreshObjectGeometry');
    },
  },
  created() {
    this.$emit('update:isValid', !!this.computedValue);
    this.$root.$on('refreshObjectGeometry', this.refreshObjectGeometry);
    this.$root.$on('addedSidebarGeometry', data => {
      if (data.inputName === this.name) {
        this.computedValue = data.geometry;
      }
    });
    this.$once('hook:beforeDestroy', this.onBeforeDestroy);
  },
};
</script>
<style lang="scss" scoped>
.geometry-input-hint {
  font-family: Roboto;
  font-style: italic;
  font-weight: 300;
  font-size: 11px;
  line-height: 13px;
  color: rgba(0, 0, 0, 0.59);
  min-height: 13px;
}
.geometry-input-value {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
}
.geometry-input-value-empty {
  color: #1a73e8;
  cursor: pointer;
  padding: 6px 12px !important;
}
.geometry-input-value-empty:hover {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  background-color: rgba(232, 240, 254, 0.7);
}
.geometry-input-value-added {
  color: #1a73e8;
  cursor: pointer;
  padding: 6px 12px !important;
}
.geometry-input-value-added:hover {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  background-color: rgba(232, 240, 254, 0.7);
}
</style>
